import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Building2, RefreshCw, Info, Plus, Pencil, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Admin › Suppliers — iter204.2 (editable).
 *
 * Operator-managed vendor directory. Add/Edit/Rename/Delete supplier
 * records. Backed by the `suppliers_directory` collection; the IMS
 * autocomplete endpoint now returns the UNION of directory names and
 * distinct in-use lot names, so anything added here appears in the
 * IMS Supplier field immediately.
 *
 * SCOPE LOCK: no merging, no automation, no scheduling. Rename is
 * rejected if the new name is already used (prevents accidental merge).
 * Delete is rejected if any active inventory lot still references the
 * supplier (prevents orphan lot names).
 */

const CARD = "bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)]";
const PRIMARY = "inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#611720] text-white text-sm font-semibold hover:bg-[#7c1e2b] transition-colors";
const SECONDARY = "inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors";

export default function AdminSuppliersDirectory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; row = edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/suppliers-directory");
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setDialogOpen(true); };

  const onDelete = async (row) => {
    const inUse = row.in_use_count || 0;
    if (inUse > 0) {
      window.alert(`"${row.name}" is still referenced by ${inUse} inventory lot(s). Reassign or remove those lots first.`);
      return;
    }
    if (!window.confirm(`Delete supplier "${row.name}"? This removes it from the directory only. If it is not in use, it will disappear from the IMS Supplier autocomplete on the next load.`)) return;
    try {
      await api.delete(`/admin/suppliers-directory/${encodeURIComponent(row.name)}`);
      toast.success(`Deleted ${row.name}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Delete failed");
    }
  };

  return (
    <div className="-mx-4 -my-4 bg-slate-100/70 min-h-[75vh]" data-testid="admin-suppliers-directory">
      <div className="px-8 py-8 max-w-[1200px] mx-auto">
        <div className="text-[12px] uppercase tracking-[0.16em] text-slate-500 font-mono mb-1">Admin › Suppliers</div>
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-[32px] leading-tight font-bold text-slate-900 tracking-tight font-serif">Suppliers</h2>
            <p className="text-[14.5px] text-slate-600 mt-1.5 max-w-3xl">
              Supplier / vendor directory used across the system. Names here also power the IMS Supplier autocomplete.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className={SECONDARY} data-testid="admin-suppliers-refresh">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={openAdd} className={PRIMARY} data-testid="admin-suppliers-add">
              <Plus size={15} strokeWidth={2.5} /> Add Supplier
            </button>
          </div>
        </div>

        <div className={`${CARD} mb-5`}>
          <div className="px-5 py-4 flex items-center justify-between border-b border-slate-200">
            <div>
              <h3 className="text-[17px] font-bold text-slate-900 tracking-tight">Vendor directory</h3>
              <p className="text-[13px] text-slate-500 mt-0.5">{rows.length} supplier{rows.length === 1 ? "" : "s"} · directory + names in use on inventory lots.</p>
            </div>
          </div>
          {loading && rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              No suppliers yet. Click <b>+ Add Supplier</b> or enter a supplier name on an IMS lot.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  {["SUPPLIER","NOTES","IN USE","SOURCE","ACTIONS"].map((h) => (
                    <th key={h} className="px-5 py-3 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.name} className="bg-white hover:bg-slate-50/40" data-testid={`admin-supplier-row-${r.name}`}>
                    <td className="px-5 py-3.5 font-semibold text-slate-900 flex items-center gap-2">
                      <Building2 size={14} className="text-slate-400" strokeWidth={1.75} /> {r.name}
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 text-[13px] max-w-[420px] truncate" title={r.notes}>{r.notes || "—"}</td>
                    <td className="px-5 py-3.5 font-mono text-[13px]">
                      {r.in_use_count > 0
                        ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold ring-1 ring-emerald-200">{r.in_use_count} lot{r.in_use_count === 1 ? "" : "s"}</span>
                        : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="px-5 py-3.5 text-[11.5px] uppercase tracking-wider text-slate-500 font-mono">{r.source}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openEdit(r)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 ring-1 ring-blue-200"
                          data-testid={`admin-supplier-edit-${r.name}`}
                        >
                          <Pencil size={11} strokeWidth={2.5} /> Edit
                        </button>
                        <button
                          onClick={() => onDelete(r)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`admin-supplier-delete-${r.name}`}
                          aria-label="Delete supplier"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-3 text-[13px] text-blue-900">
          <Info size={16} className="text-blue-600 shrink-0 mt-0.5" strokeWidth={2.5} />
          <div>
            <b>Directory vs. Scrape.</b> This page manages the supplier names used across BBK — IMS autocomplete pulls from here. To monitor supplier websites for pricing or stock changes, use <b>Supplier Scrape</b>.
            <br />
            <span className="text-[12px] text-slate-700">Rename applies to every inventory lot that references the old name. Delete is blocked while any active lot still references a supplier.</span>
          </div>
        </div>
      </div>

      {dialogOpen && (
        <SupplierDialog
          row={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={async () => { setDialogOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

function SupplierDialog({ row, onClose, onSaved }) {
  const isEdit = !!row;
  const [name, setName]   = useState(row?.name || "");
  const [notes, setNotes] = useState(row?.notes || "");
  const [saving, setSaving] = useState(false);

  const canSubmit = name.trim().length > 0;

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/admin/suppliers-directory/${encodeURIComponent(row.name)}`, {
          name: name.trim(),
          notes: notes.trim(),
        });
        if (data.renamed) toast.success(`Renamed to "${data.supplier.name}" · ${data.lots_renamed} lot(s) updated`);
        else              toast.success(`Updated ${data.supplier.name}`);
      } else {
        await api.post("/admin/suppliers-directory", { name: name.trim(), notes: notes.trim() });
        toast.success(`Added ${name.trim()}`);
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 backdrop-blur-sm px-4 py-8" onClick={onClose} data-testid="admin-supplier-dialog">
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-200">
          <div>
            <h3 className="text-[19px] font-bold text-slate-900 tracking-tight">{isEdit ? "Edit Supplier" : "Add Supplier"}</h3>
            <p className="text-[13px] text-slate-500 mt-1">{isEdit ? "Rename applies to every inventory lot that references the old name." : "Names entered here appear in the IMS Supplier autocomplete."}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100" aria-label="Close" data-testid="admin-supplier-dialog-close">
            <X size={17} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[13px] font-semibold text-slate-800 mb-1.5">Supplier Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 text-sm" placeholder="e.g. OPTIMIZED INSIDER" data-testid="admin-supplier-dialog-name" autoFocus />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-slate-800 mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full min-h-[100px] p-2.5 text-sm border border-slate-300 rounded-md focus:border-[#611720] focus:ring-1 focus:ring-[#611720] outline-none resize-y"
              placeholder="Optional — freight terms, contact, anything else"
              maxLength={2000}
              data-testid="admin-supplier-dialog-notes"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className={SECONDARY} data-testid="admin-supplier-dialog-cancel">Cancel</button>
          <button onClick={submit} disabled={!canSubmit || saving} className={`${PRIMARY} disabled:opacity-50`} data-testid="admin-supplier-dialog-save">
            {saving ? "Saving…" : (isEdit ? "Save Changes" : "Add Supplier")}
          </button>
        </div>
      </div>
    </div>
  );
}
